import * as React from 'react'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Typography from '@mui/joy/Typography'
import Alert from '@mui/joy/Alert'
import { CircularProgress } from '@mui/joy'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { documentProcessingTestApi, SelectedDocument } from '../api'

interface Props {
    onSelect: (doc: SelectedDocument) => void
}

/**
 * Tab 3 — upload a brand-new real file (PDF/image). Streams straight to GCS
 * server-side while hashing in the same pass (never buffered), and returns a
 * real gcsUri + contentHash — the only place in this page where a document
 * doesn't already exist somewhere.
 */
export default function DocumentUploader({ onSelect }: Props) {
    const fileInputRef = React.useRef<HTMLInputElement>(null)
    const [file, setFile] = React.useState<File | null>(null)
    const [projectName, setProjectName] = React.useState('')
    const [uploading, setUploading] = React.useState(false)
    const [error, setError] = React.useState<string | null>(null)

    const canUpload = !!file && projectName.trim() && !uploading

    const handleUpload = async () => {
        if (!file || !projectName.trim()) return
        setUploading(true)
        setError(null)
        try {
            const result = await documentProcessingTestApi.uploadDocument(file, projectName.trim())
            onSelect({ contentHash: result.contentHash, blobUri: result.gcsUri, filename: result.filename, projectName: projectName.trim() })
        } catch {
            setError('Upload failed — check the file type (PDF, JPEG, PNG only) and size (max 25MB)')
        } finally {
            setUploading(false)
        }
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 480 }}>
            <FormControl size="sm">
                <FormLabel>Project Name</FormLabel>
                <Input size="sm" value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder="the tenant's project name" />
            </FormControl>

            <FormControl size="sm">
                <FormLabel>File</FormLabel>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf,image/jpeg,image/png"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                    style={{ display: 'none' }}
                />
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button variant="outlined" size="sm" startDecorator={<UploadFileIcon />} onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                        {file ? 'Change File' : 'Select File'}
                    </Button>
                    {file && (
                        <Typography level="body-xs" color="neutral">
                            {file.name} ({Math.round(file.size / 1024)} KB)
                        </Typography>
                    )}
                </Box>
            </FormControl>

            <Box>
                <Button size="sm" variant="solid" color="primary" disabled={!canUpload} onClick={handleUpload}>
                    {uploading ? <CircularProgress size="sm" /> : 'Upload'}
                </Button>
            </Box>

            {error && (
                <Alert color="danger" variant="soft">
                    {error}
                </Alert>
            )}
        </Box>
    )
}
